import {
  useState,
  useCallback,
  createContext,
  useContext,
  PropsWithChildren,
  FC,
} from "react";
import { Severity, Notification } from "../types";
import { Alert, AlertTitle, IconButton, AlertColor } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Define the context shape
interface NotificationContextType {
  notifications: Notification[];
  addNotification: (message: string, severity: Severity) => void;
  removeNotification: (id: string) => void;
}

const defaultContextValue: NotificationContextType = {
  notifications: [],
  addNotification: () => console.warn("addNotification not implemented"),
  removeNotification: () => console.warn("removeNotification not implemented"),
};

// Create a context for notifications
const NotificationContext =
  createContext<NotificationContextType>(defaultContextValue);

// Use this hook to access the context
export function useNotification(): NotificationContextType {
  return useContext(NotificationContext);
}

export const NotificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((currentNotifications: Notification[]) =>
      currentNotifications.filter((notif) => notif.id !== id)
    );
  }, []);

  const addNotification = useCallback(
    (message: string, severity: Severity) => {
      const newNotification: Notification = {
        id: Math.random().toString(36),
        message: `${message}`,
        severity,
      };
      setNotifications((currentNotifications: Notification[]) => [
        ...currentNotifications,
        newNotification,
      ]);

      // Remove the notification after 5 seconds if not error
      if (severity !== "error") {
        setTimeout(() => {
          removeNotification(newNotification.id);
        }, 5000);
      }
    },
    [removeNotification]
  );

  return (
    <NotificationContext.Provider
      value={{
        addNotification,
        removeNotification,
        notifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const Notifications = () => {
  const { notifications } = useNotification();
  const notificationsCopy = notifications ?? [];
  const rendered = notificationsCopy.map((item) => (
    <Alert
      key={item.id}
      severity={item.severity as AlertColor}
      action={<RemoveButton item_id={item.id} />}
      sx={{ margin: "1em", width: "600px" }}
    >
      <AlertTitle>{item.severity.toLocaleUpperCase()}</AlertTitle>
      {item.message}
    </Alert>
  ));

  return <div>{rendered}</div>;
};

const RemoveButton = (props: { item_id: string }) => {
  const { removeNotification } = useNotification();
  return (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={() => removeNotification(props.item_id)}
    >
      <CloseIcon fontSize="inherit" />
    </IconButton>
  );
};
